import { Button, Card, CardContent, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";
import moment from "moment";
import { get, pick } from "radash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import AuthService from "src/services/authService";
import UserService from "src/services/userService";
import { useQuery } from "src/services/utils";
import { updateAuthUser } from "src/store/actions/auth";
import { IAppState } from "src/store/reducers";
import { AuthState } from "src/store/reducers/auth";

import FormHeaderSection from "@components/FormHeaderSection";
import LoadingButton from "@components/LoadingButton";
import StepTracker from "@components/StepTracker/StepTracker";

import SleepCard from "@containers/Cards/SleepCard";
import RegistrationForm from "@containers/Forms/RegistrationForm";
import PatientDetailsForm, {
  PATIENT_DETAILS_FORM_ID,
} from "@containers/Forms/RegistrationForm/PatientDetailsForm";
import { REGISTRATION_FORM_ID } from "@containers/Forms/RegistrationForm/RegistrationForm";
import { EventInfo } from "@containers/ProviderProfile/ProviderProfile";

import { getRecordId, handleDynamicFormSubmission } from "@utils/helpers";

import NoAvailabilityPrompt from "./NoAvailabilityPrompt";
import ScheduleAppointment from "./ScheduleAppointment";

const STEPS = {
  register: 1,
  schedule: 2,
  details: 3,
  confirmation: 4,
};

const totalSteps = Object.values(STEPS).length;

const getUserFields = (user: any) => {
  const insuranceCarrier = get(user, "insuranceCards.0.insuranceCarrier", "");
  return {
    ...pick(user, [
      "firstName",
      "lastName",
      "state",
      "dateOfBirth",
      "timeZone",
    ]),
    insuranceCarrier,
  };
};

const NoEmailAuthRegistration = ({
  setInitialData,
}: {
  setInitialData: (data: object) => void;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();
  const providerId = query.get("providerId");
  const { authUser: user } = useSelector<IAppState, AuthState>((s) => s.auth);

  const [bridgeProviders, setBridgeProviders] = useState<string[]>([]);
  const [step, setStep] = useState(() => STEPS.register);
  const [schedulingData, setSchedulingData] = useState<any>(undefined);
  const [formData, setFormData] = useState<Record<string, any>>(() =>
    getUserFields(user),
  );

  const [submitting, setSubmitting] = useState(false);
  const [noAvailability, setNoAvailability] = useState(false);

  useEffect(() => {
    if (!user) return history.replace("/");
    setInitialData(formData);
  }, [formData]);

  const updateStep = (increment: boolean) => {
    if (!increment && noAvailability && step === STEPS.schedule) {
      setNoAvailability(false);
    }

    if (step === totalSteps) return null;
    if (increment) setStep(step + 1);
    else setStep(step - 1);
  };

  const createAppointment = async () => {
    const { user } = AuthService.getAuth();

    const startTime = schedulingData?.start;
    const endTime = schedulingData?.end;
    const provider = schedulingData?.provider;
    let body: any = {
      endTime,
      startTime,
      description: "Consultation",
      inviteeEmail: provider?.email,
      at: dayjs(endTime).format("hh:mm a"),
      on: dayjs(startTime).format("MMM DD, YYYY"),
    };

    await UserService.createAppointment({
      ...body,
      patientId: getRecordId(user),
      providerId: getRecordId(provider),
    })
      .then((res) => {
        setSubmitting(false);
        setStep(STEPS.confirmation);
      })
      .catch((err) => {
        setSubmitting(false);
        toast.error(err.message || "Unable to create appointment");
      });
  };

  const getPatientInfo = (data: Record<string, any>) => {
    const carrier = data?.insuranceCarrier;
    let insuranceCards: any = get(user, "insuranceCards", []);

    if (get(insuranceCards, "0.insuranceCarrier", "") !== carrier) {
      insuranceCards = [{ insuranceCarrier: carrier }];
    }

    return {
      insuranceCards,
      state: data?.state || "",
      lastName: data?.lastName || "",
      firstName: data?.firstName || "",
      timeZone: schedulingData?.timeZone || data?.timeZone || "",
      chatbotProgress: {
        sheepa: 16,
        sheepaFinished: false,
        sheepaTime: moment().utc(),
      },
    };
  };

  const updatePatientProfile = async (values: any) => {
    setSubmitting(true);
    const patientId = getRecordId(user);

    await UserService.updateProfile(values, patientId)
      .then(async (res) => {
        AuthService.updateUser(res.data);
        dispatch(updateAuthUser(res.data));
        updateStep(true);
      })
      .catch((error) => toast.error(error.message || "Unable to save details"))
      .finally(() => setSubmitting(false));
  };

  const handleSoftEligibilityCheck = async (data: any) => {
    if (step === 1) {
      const { state, insuranceCarrier } = data;
      await UserService.getSoftEligibility({
        state,
        carrier: insuranceCarrier,
      })
        .then((res) => {
          setBridgeProviders(get(res, "data.providerIds", []));
        })
        .catch(() => {});
    }
  };

  const eventStart = get(schedulingData, "start", "");
  const eventEnd = get(schedulingData, "end", "");
  const tz = get(schedulingData, "timeZone", "");

  return (
    <Box sx={{ padding: 1 }}>
      <Box paddingTop={0.5} paddingBottom={2.5}>
        <StepTracker
          totalSteps={totalSteps}
          currentStep={step}
          barHeight={6}
          gap={1}
        />
      </Box>

      <Grid container spacing={3}>
        {step === 1 && (
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <SleepCard />
            </Stack>
          </Grid>
        )}

        {step === 1 && (
          <Grid item xs={12} md={6}>
            <FormHeaderSection
              title="Welcome to Ognomy Sleep"
              description="Please complete your details to begin your sleep journey"
            />
            <RegistrationForm
              data={pick(formData, [
                "state",
                "firstName",
                "lastName",
                "insuranceCarrier",
              ])}
              onSubmit={async (data) => {
                await handleSoftEligibilityCheck(data);
                const updatedFormData = {
                  ...formData,
                  ...data,
                };

                setFormData(updatedFormData);
                await updatePatientProfile(getPatientInfo(updatedFormData));
                updateStep(true);
              }}
            />
          </Grid>
        )}

        {/* Ensures scheduling component is rendered on the left at all times */}
        {step === 2 && !noAvailability && formData?.state && (
          <Grid item xs={12}>
            <Typography
              variant="h5"
              fontSize={28}
              fontWeight={600}
              textAlign="center"
              pb={1}
            >
              Hi {formData.firstName}!
            </Typography>
            <Typography
              variant="h5"
              fontSize={28}
              fontWeight={600}
              textAlign="center"
              pb={1}
            >
              Meet Your Recommended Sleep Specialist.
            </Typography>
            <ScheduleAppointment
              hideButtons
              state={formData?.state}
              providerId={providerId}
              providerIds={bridgeProviders}
              timeZone={formData?.timeZone}
              onNoAvailability={() => setNoAvailability(true)}
              onSelect={(data) => {
                updateStep(true);
                setSchedulingData(data);
                setFormData((state) => ({ ...state, timeZone: data.timeZone }));
              }}
            />
          </Grid>
        )}

        {step === 2 && noAvailability && (
          <Grid item xs={12} justifyItems="center">
            <NoAvailabilityPrompt onSubmit={() => updateStep(true)} />
          </Grid>
        )}

        {step > 2 && step <= 3 && (
          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontSize={28} fontWeight={600} pb={1}>
              Create an Account to Confirm Your Appointment
            </Typography>
            <EventInfo
              tz={tz}
              eventEnd={eventEnd}
              eventStart={eventStart}
              provider={schedulingData?.provider}
            />
          </Grid>
        )}

        {step === 3 && (
          <Grid item xs={12} md={6}>
            <PatientDetailsForm
              data={pick(formData, [
                "dateOfBirth",
                "phone",
                "address",
                "city",
                "zipcode",
                "gender",
                "timeZone",
              ])}
              onSubmit={async (data) => {
                setFormData((state) => ({ ...state, ...data }));
                await updatePatientProfile(data);
                await createAppointment();
              }}
            />
          </Grid>
        )}

        {step === 4 && (
          <Grid item xs={12} justifyItems="center">
            <Card sx={{ maxWidth: 700, padding: 2 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" pb={2} fontWeight={600}>
                  Welcome to Ognomy Sleep
                </Typography>
                <Typography pb={1}>
                  We have reserved your appointment slot for the next 72 hours.
                  To see the sleep specialist, please complete your profile now
                  or via the email reminder. If not completed within 72 hours,
                  your slot may be released and given to another patient.
                </Typography>
              </CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() =>
                    history.replace("/complete-profile?step=insurance")
                  }
                >
                  Complete My Profile
                </Button>
                <Button
                  variant="text"
                  onClick={() => history.replace("/patientDashboardPage")}
                >
                  I'll Finish Later
                </Button>
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>

      {step !== totalSteps && (
        <Stack spacing={1} direction="row-reverse" sx={actionBtnStyles}>
          <LoadingButton
            type="submit"
            disabled={step === 2 && !eventStart && totalSteps === step}
            variant="contained"
            loading={submitting}
            onClick={() => {
              if (step === 1) {
                handleDynamicFormSubmission(REGISTRATION_FORM_ID);
              } else if (step === 3) {
                handleDynamicFormSubmission(PATIENT_DETAILS_FORM_ID);
              } else setStep(step + 1);
            }}
          >
            Continue
          </LoadingButton>
          {step > 1 && (
            <LoadingButton
              type="submit"
              variant="contained"
              onClick={() => {
                updateStep(false);
                if (step === 2) setSchedulingData(undefined);
              }}
            >
              Back
            </LoadingButton>
          )}
        </Stack>
      )}
    </Box>
  );
};

const actionBtnStyles = {
  padding: 1,
  paddingBottom: 1.5,
  bottom: 0,
  left: 0,
  position: "fixed",
  width: "100%",
  zIndex: 100,
  backgroundColor: "background.default",
  justifyContent: "space-between",
};

export default NoEmailAuthRegistration;
